import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { MdEdit, MdDelete } from "react-icons/md";
import { FaShieldAlt, FaUserShield } from "react-icons/fa";
import { Button, Badge } from "react-bootstrap";
import { emailDomainAuthenticatorActions } from "../../actions/emailDomainAuthenticator.actions";
import Utils from "../../utils";
import Title from "../common/Title";
import EnhancedGrid from "../common/EnhancedGrid";
import GridHelper from "../helpers/gridHelper";
import ConfirmModal from "../common/ConfirmModal";

function ArmaninoSSO({ user, onEditAuthenticator, onAddAuthenticator, hideTitle }) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [confirmDeleteModalShow, setConfirmDeleteModalShow] = useState(false);
  const [deleteAuthenticatorName, setDeleteAuthenticatorName] = useState("");
  const [deleteAuthenticatorId, setDeleteAuthenticatorId] = useState("");
  const [selectedAuthenticator, setSelectedAuthenticator] = useState(null);

  const emailDomainAuthenticator = useSelector(
    (state) => state.emailDomainAuthenticator
  );

  const refresh = () => {
    // Filter for Armanino domains
    const queryParams = {
      emailDomain: "armanino",
      ...Utils.getQueryStringParams(location),
    };

    dispatch(emailDomainAuthenticatorActions.get(queryParams));
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line
  }, [location]);

  // Refresh the grid after a create/update (e.g. from the Add/Edit window) or delete completes.
  useEffect(() => {
    if (emailDomainAuthenticator.success) {
      refresh();
    }
    // eslint-disable-next-line
  }, [emailDomainAuthenticator.success]);

  useEffect(() => {
    if (emailDomainAuthenticator.deleteSuccess) {
      refresh();
      setSelectedAuthenticator(null);
    }
    // eslint-disable-next-line
  }, [emailDomainAuthenticator.deleteSuccess]);

  const handleDeleteAuthenticator = () => {
    setConfirmDeleteModalShow(false);
    const requestParameters = {
      id: deleteAuthenticatorId,
    };

    dispatch(emailDomainAuthenticatorActions.remove(requestParameters));
  };

  const displayColumns = ["emailDomain", "authenticatorType", "clientId", "createdOn"];

  const data = emailDomainAuthenticator.paginatedResult?.data
    ?.filter(a =>
      a.emailDomain?.toLowerCase().includes("armanino")
    )
    .map((a) => ({
      ...a,
      createdOn: Utils.convertToDateOnly(a.createdOn),
    })) || [];

  const getAuthTypeBadge = (type) => {
    switch(type) {
      case "Okta":
        return <Badge bg="primary">Okta</Badge>;
      case "AzureAD":
        return <Badge bg="info">Azure AD</Badge>;
      case "EntraID":
        return <Badge bg="success">Entra ID</Badge>;
      default:
        return <Badge bg="secondary">{type}</Badge>;
    }
  };

  return (
    <>
      {!hideTitle && (
        <Title
          text={"Armanino SSO Configuration"}
          icon={<FaShieldAlt className="mb-1" />}
        />
      )}

      {/* Action Buttons Bar */}
      <div
        className="d-flex gap-2 mb-3 p-3 bg-light rounded"
        style={{
          opacity: selectedAuthenticator ? 1 : 0.6,
          transition: 'opacity 0.3s ease'
        }}
      >
        <Button
          variant="outline-primary"
          size="sm"
          disabled={!selectedAuthenticator}
          onClick={() => {
            if (!selectedAuthenticator) return;
            if (onEditAuthenticator) {
              onEditAuthenticator(selectedAuthenticator.id);
            } else {
              navigate(`/client/authenticators/edit?id=${selectedAuthenticator.id}`);
            }
          }}
        >
          <MdEdit className="mb-1 me-2" />
          Edit
        </Button>

        <Button
          variant="outline-danger"
          size="sm"
          disabled={!selectedAuthenticator}
          onClick={() => {
            if (selectedAuthenticator) {
              setDeleteAuthenticatorId(selectedAuthenticator.id);
              setDeleteAuthenticatorName(selectedAuthenticator.emailDomain);
              setConfirmDeleteModalShow(true);
            }
          }}
        >
          <MdDelete className="mb-1 me-2" />
          Delete
        </Button>

        <Button
          variant="primary"
          size="sm"
          className="ms-auto"
          onClick={() => (onAddAuthenticator ? onAddAuthenticator() : navigate('/client/authenticators/add'))}
        >
          Add Authenticator
        </Button>

        {selectedAuthenticator && (
          <div className="align-self-center">
            <small className="text-muted">
              Selected: <strong>{selectedAuthenticator.emailDomain}</strong>
              {" - "}
              {getAuthTypeBadge(selectedAuthenticator.authenticatorType)}
            </small>
          </div>
        )}
      </div>

      <EnhancedGrid
        gridId="armaninosso"
        data={data}
        displayColumns={displayColumns}
        idColumn={"id"}
        pageNumber={(emailDomainAuthenticator.paginatedResult?.pageNumber ?? 1) - 1}
        totalPages={emailDomainAuthenticator.paginatedResult?.totalPages ?? 0}
        loading={emailDomainAuthenticator.loading}
        onPageChange={GridHelper.handlePageClick}
        onSort={GridHelper.handleSortClick}
        onRowClick={setSelectedAuthenticator}
        selectedRow={selectedAuthenticator}
        emptyState={{
          icon: <FaUserShield size={80} className="text-muted mb-3" />,
          message: "No SSO configurations found",
          subtitle: "SSO configurations will appear here.",
        }}
      />

      <ConfirmModal
        show={confirmDeleteModalShow}
        title="Delete SSO Authenticator"
        question={`Are you sure you want to delete the SSO authenticator for ${deleteAuthenticatorName}?`}
        noButtonText="Cancel"
        yesButtonText="Yes, Delete"
        handleClose={() => setConfirmDeleteModalShow(false)}
        handleConfirm={handleDeleteAuthenticator}
      />
    </>
  );
}

export default ArmaninoSSO;
